import React, { useEffect, useState } from 'react';
import { api, AED_short } from '../lib/api';
import { TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react';

/**
 * Compact 5-year median AED/sqft trendline — sits on the Trends page.
 * Props:
 *   community?: string  filter to one community (default: all)
 *   txType?: 'sale' | 'rent'
 *   years?: number  default 6
 */
export default function YearlyHeatmap({ community, txType = 'sale', years = 6 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    const params = { tx_type: txType, years };
    if (community) params.community = community;
    api.get('/trends/yearly-heatmap', { params })
      .then(r => { if (mounted) setData(r.data); })
      .catch(e => { if (mounted) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [community, txType, years]);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-center h-40" data-testid="yearly-heatmap-loading">
        <Loader2 size={20} className="animate-spin text-slate-400"/>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-white border border-red-200 rounded-2xl p-4 text-sm text-red-600" data-testid="yearly-heatmap-error">
        {error}
      </div>
    );
  }
  if (!data?.series?.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-4 text-sm text-slate-400">
        No historical data for this filter.
      </div>
    );
  }

  const series = data.series;
  const validPsf = series.filter(s => s.median_psf).map(s => s.median_psf);
  const maxPsf = Math.max(...validPsf, 1);
  const minPsf = Math.min(...validPsf, 0);
  const rangePsf = Math.max(maxPsf - minPsf, 1);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5" data-testid="yearly-heatmap">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#679286] font-semibold mb-1">
            Yearly median · {data.tx_type === 'rent' ? 'rent' : 'sale'} AED/sqft
          </div>
          <h3 className="font-serif text-lg text-slate-900 leading-tight">
            {data.community}
          </h3>
        </div>
      </div>

      {/* Bar row */}
      <div className="grid gap-2 mb-3" style={{ gridTemplateColumns: `repeat(${series.length}, minmax(0,1fr))` }}>
        {series.map((s) => {
          const hasData = s.median_psf != null;
          const heightPct = hasData ? ((s.median_psf - minPsf) / rangePsf) * 100 : 0;
          const yoy = s.yoy_pct;
          return (
            <div key={s.year} className="flex flex-col items-center gap-1" data-testid={`heatmap-year-${s.year}`}>
              {/* Bar */}
              <div className="w-full h-24 flex items-end">
                {hasData ? (
                  <div
                    className="w-full rounded-t-md bg-gradient-to-b from-[#679286] to-[#557568] relative group cursor-help transition-all duration-200 hover:from-[#7ba597]"
                    style={{ height: `${Math.max(heightPct, 5)}%` }}
                    title={`${s.year}: ${Math.round(s.median_psf).toLocaleString()} AED/sqft · ${s.volume.toLocaleString()} sales`}
                  />
                ) : (
                  <div className="w-full h-1 bg-slate-100 rounded"/>
                )}
              </div>
              {/* Value */}
              <div className="text-[10px] tabular-nums text-slate-700 font-semibold">
                {hasData ? Math.round(s.median_psf).toLocaleString() : '—'}
              </div>
              {/* YoY badge */}
              <div className="text-[9px]">
                {yoy == null ? <span className="text-slate-300">—</span>
                : yoy > 0 ? (
                    <span className="text-emerald-700 inline-flex items-center gap-0.5">
                      <TrendingUp size={9}/>+{yoy}%
                    </span>
                  )
                : yoy < 0 ? (
                    <span className="text-rose-700 inline-flex items-center gap-0.5">
                      <TrendingDown size={9}/>{yoy}%
                    </span>
                  )
                : (
                    <span className="text-slate-400 inline-flex items-center gap-0.5">
                      <Minus size={9}/>0%
                    </span>
                  )
                }
              </div>
              {/* Year label */}
              <div className="text-[10px] text-slate-500 tabular-nums">
                {s.year}
              </div>
              {/* Volume label */}
              <div className="text-[9px] text-slate-400 tabular-nums">
                {s.volume >= 1000 ? `${(s.volume/1000).toFixed(1)}k` : s.volume} sales
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-[10px] text-slate-400 border-t border-slate-100 pt-2">
        Median AED/sqft per year · YoY delta shown below each bar · source: DLD historical + Bayut recent
      </div>
    </div>
  );
}
